import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Loading from '@/components/common/Loading'
import {
  listAiLabModels,
  fetchRecentAiLabRuns,
} from '@/lib/aiLabClient'
import {
  AI_LAB_ENABLED,
  DEFAULT_AI_LAB_MODELS,
  DEFAULT_AI_LAB_LEVEL,
} from '@/lib/config'

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—'
  return `$${Number(value).toFixed(6)}`
}

const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function AILabHome() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [models, setModels] = useState([])
  const [runs, setRuns] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    const load = async () => {
      if (!AI_LAB_ENABLED) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const [modelData, runData] = await Promise.all([
          listAiLabModels(),
          fetchRecentAiLabRuns(10),
        ])
        setModels(modelData)
        setRuns(runData)
      } catch (err) {
        console.error('Failed to load AI Lab data', err)
        toast.error('Unable to load AI Lab data')
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  const defaultModelSet = useMemo(() => new Set(DEFAULT_AI_LAB_MODELS), [])
  const [showAllModels, setShowAllModels] = useState(false)

  const filteredModels = useMemo(() => {
    if (showAllModels) return models
    return models.filter((model) => defaultModelSet.has(model.id))
  }, [models, showAllModels, defaultModelSet])

  if (!AI_LAB_ENABLED) {
    return (
      <Container>
        <Card className="p-8">
          <h1 className="text-3xl font-semibold mb-4">AI Lab</h1>
          <p className="text-brand-slate">
            The AI Lab feature is currently disabled. Update the environment flag `AI_LAB_ENABLED` to access this
            workspace.
          </p>
        </Card>
      </Container>
    )
  }

  if (loading) {
    return (
      <Container>
        <Card className="p-12">
          <Loading text="Loading AI Lab data..." />
        </Card>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Card className="p-8">
          <h1 className="text-2xl font-semibold mb-4">AI Lab</h1>
          <p className="text-brand-slate mb-4">
            We ran into an issue loading the AI Lab dashboard. Please try again.
          </p>
          <Button onClick={() => navigate(0)}>Retry</Button>
        </Card>
      </Container>
    )
  }

  return (
    <Container className="py-10 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-brand-slate">AI Lab</h1>
          <p className="text-brand-slate-dark mt-2 max-w-2xl">
            Compare OpenRouter models against Minnesota Squirt B1 schedule discovery tasks. Configure a run, execute
            the six-scenario test suite, and review aggregated scores, latency, and cost.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="green">Default Level: {DEFAULT_AI_LAB_LEVEL}</Badge>
          <Button onClick={() => navigate('/admin/ai-lab/run')}>Start New Run</Button>
        </div>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-slate">Model Catalog</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-brand-slate-dark">
              Showing {filteredModels.length} of {models.length} models
            </span>
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => setShowAllModels(!showAllModels)}
            >
              {showAllModels ? 'Show Default Only' : 'Show All Models'}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left table-fixed">
            <thead>
              <tr className="text-brand-slate-dark text-sm">
                <th className="py-2 pr-4">Model</th>
                <th className="py-2 pr-4">Provider</th>
                <th className="py-2 pr-4">Prompt $</th>
                <th className="py-2 pr-4">Completion $</th>
                <th className="py-2 pr-4">Context</th>
                <th className="py-2 pr-4">Default</th>
              </tr>
            </thead>
            <tbody>
              {filteredModels.map((model) => (
                <tr key={model.id} className="border-t border-brand-gray-100 text-brand-slate">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-sm">{model.name}</div>
                    <div className="text-xs text-brand-slate-dark break-all">{model.id}</div>
                  </td>
                  <td className="py-2 pr-4 text-sm">{model.provider || '—'}</td>
                  <td className="py-2 pr-4 text-sm">{formatCurrency(model.pricing?.prompt)}</td>
                  <td className="py-2 pr-4 text-sm">{formatCurrency(model.pricing?.completion)}</td>
                  <td className="py-2 pr-4 text-sm">{model.contextLength ? `${model.contextLength.toLocaleString()} tokens` : '—'}</td>
                  <td className="py-2 pr-4 text-sm">
                    {defaultModelSet.has(model.id) ? <Badge variant="green">Default</Badge> : <Badge>Optional</Badge>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-brand-slate">Recent Runs</h2>
          <Button variant="secondary" onClick={() => navigate('/admin/ai-lab/run')}>
            Configure New Run
          </Button>
        </div>
        {runs.length === 0 ? (
          <div className="text-brand-slate-dark text-sm p-6 bg-brand-gray-50 rounded-lg">
            No runs recorded yet. Launch a run to compare models across the six scenarios.
          </div>
        ) : (
          <div className="space-y-3">
            {runs.map((run) => {
              const isComplete = Boolean(run.completed_at)
              const statusLabel = isComplete ? 'Completed' : 'In Progress'
              return (
                <div
                  key={run.id}
                  className="flex flex-wrap items-center justify-between gap-3 border border-brand-gray-100 rounded-lg p-4"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-brand-slate text-sm">{formatDateTime(run.started_at)}</span>
                      <Badge variant={isComplete ? 'green' : 'amber'}>{statusLabel}</Badge>
                    </div>
                    <div className="text-xs text-brand-slate-dark mt-1">
                      {run.model_list?.length || 0} models · {run.scenario_list?.length || 0} scenarios ·{' '}
                      {run.association_list?.length || 0} associations
                      {run.avg_score !== null && run.avg_score !== undefined && (
                        <span> · Avg Score: <span className="font-semibold text-brand-slate">{run.avg_score.toFixed(1)}%</span></span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Link
                      className="text-brand-blue text-sm font-medium hover:underline"
                      to={`/admin/ai-lab/results?runId=${run.id}`}
                    >
                      View Results
                    </Link>
                    {!isComplete && (
                      <span className="text-xs text-brand-slate-dark">
                        Last updated: {formatDateTime(run.updated_at || run.started_at)}
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </Container>
  )
}

export default AILabHome


